import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Host,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';

import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { GasUploaderDirective } from '../../utils/gas-uploader-directive/gas-uploader.directive';
import { GasFileMode, GasFileModeType } from './gas-file-mode.enum';
import { getFileNames } from './gas-file-uploader.helper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';


const FULL_PROGRESS = 100;

/**
 * @see https://www.figma.com/file/yqBeq1MiGfpdN62SvmA8tg/RTLABS_GAS_DS_1?node-id=3539%3A37027&viewport=24%2C70%2C0.070626400411129
 */
@UntilDestroy()
@Component({
  selector: 'gas-file-uploader',
  templateUrl: './gas-file-uploader.component.html',
  styleUrls: ['./gas-file-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GasUploaderDirective,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GasUploaderDirective),
      multi: true,
    },
  ],
})
export class GasFileUploaderComponent implements OnChanges, OnInit, ControlValueAccessor {
  @Input()
  public placeholder = 'Выберите файл';

  @Input()
  public fileName: string;

  @Input()
  public successText = 'Файл загружен';

  @Input()
  public errorText: string;

  @Input()
  public progress = 0;

  @Input()
  public accept = '';

  @Input()
  public mode: GasFileMode | GasFileModeType = GasFileMode.Single;

  public fileNames$: Observable<string | string[]> = this.gasUploaderDirective.onChangeFile$.pipe(
    map((files: File | File[]) => {
      if (this.mode === GasFileMode.Single) {
        return Array.isArray(files) ? files[0] : files;
      }

      return files;
    }),
    map((files: File | File[]) => Array.isArray(files) ? files.map((file: File) => file?.name) : files?.name),
    map((names: string[] | string) => getFileNames(names)),
  );

  public successUpload$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Host() private gasUploaderDirective: GasUploaderDirective,
    @Self() @Optional() private ngControl: NgControl,
  ) {
  }

  public ngOnInit(): void {
    this.gasUploaderDirective.multi = this.mode === GasFileMode.Multi;
    this.gasUploaderDirective.isDragAndDrop = true;
    this.gasUploaderDirective.accept = this.accept;
    this.gasUploaderDirective.ngOnInit();

    this.ngControl?.control?.valueChanges
      .pipe(
        untilDestroyed(this),
        filter((value: File | File[]) => Array.isArray(value) ? !value.length : !value),
      ).subscribe((value: File | File[]) => {
        this.gasUploaderDirective.onChangeFile$.next(value);
        this.successUpload$.next(false);
      });
  }

  public ngOnChanges(): void {
    if (this.errorText?.trim()) {
      this.successUpload$.next(false);
      this.progress = 0;
    }

    if (this.progress === FULL_PROGRESS) {
      this.successUpload$.next(true);
      this.progress = 0;
      this.errorText = '';
    }
  }

  public selectFile(): void {
    this.gasUploaderDirective.selectFile();
  }

  public getFileName(successUpload: boolean, fileNames: string | string[]): string {
    if (successUpload || this.errorText) {
      return this.placeholder;
    }

    const names = Array.isArray(fileNames) ? fileNames.join(' ') : fileNames;

    return this.fileName || names;
  }

  public isSingleMode(): boolean {
    return this.mode === GasFileMode.Single;
  }

  public writeValue(value: File): void {
    this.onChange(value);
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  public onChange = (value: File) => {
  };

  public onTouched = () => {
  };
}
